import { ClerkProvider, useClerk } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Splash } from "@/components/app/splash";
import { type AuthSnapshot, useAuthSnapshot, useOrgChangeFlush } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { initSentry } from "@/lib/sentry";
import { ThemeProvider } from "@/lib/theme";
import { makeRouter, queryClient } from "@/router";
import "../tailwind.css";

initSentry();

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) throw new Error("VITE_CLERK_PUBLISHABLE_KEY missing at build time");

const placeholderAuth: AuthSnapshot = {
  isLoaded: false,
  isSignedIn: false,
  userId: null,
  orgId: null,
  getToken: async () => null,
};
const router = makeRouter(placeholderAuth);

function AppShell() {
  const auth = useAuthSnapshot();
  const clerk = useClerk();
  useOrgChangeFlush(auth.orgId, queryClient);
  if (!clerk.loaded || !auth.isLoaded) return <Splash />;
  return <RouterProvider router={router} context={{ queryClient, auth }} />;
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}
createRoot(rootElement).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        variables: {
          colorPrimary: "#d4a853",
          borderRadius: "0.25rem",
          fontFamily: '"Outfit", system-ui, sans-serif',
          colorBackground: "#161412",
          colorText: "#f5f0e6",
          colorTextSecondary: "#bcb5a8",
          colorInputBackground: "#1e1c19",
          colorInputText: "#f5f0e6",
          colorNeutral: "#f5f0e6",
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <I18nProvider>
            <AppShell />
          </I18nProvider>
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
);
